import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAuth, API } from "@/App";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { toast } from "sonner";
import axios from "axios";
import { motion } from "framer-motion";
import { 
  Users,
  Clock,
  AlertCircle,
  CheckCircle2,
  Check,
  Mail,
  Loader2,
  Copy,
  ChevronRight,
  ExternalLink,
  Bell,
  Plus,
  CircleDot,
  Trophy,
  XCircle,
  Zap,
  CreditCard,
  Sparkles,
  ListChecks,
  X,
  FileEdit,
  Kanban,
  Calendar,
  Target,
  ClipboardList,
  PlayCircle,
  PauseCircle,
  TrendingUp,
  Send,
  UserPlus,
  UserPlus2
} from "lucide-react";
import AppSidebar from "@/components/AppSidebar";
import EmailVerificationBanner from "@/components/EmailVerificationBanner";
import UsageWarningBanner from "@/components/UsageWarningBanner";

// Lead Status configuration (pre-booking) - softer, more premium colors
const leadStatusConfig = {
  new: { color: "bg-blue-50 text-blue-600", label: "New", icon: CircleDot },
  contacted: { color: "bg-amber-50 text-amber-600", label: "Contacted", icon: Mail },
  qualified: { color: "bg-violet-50 text-violet-600", label: "Qualified", icon: CheckCircle2 },
  won: { color: "bg-emerald-50 text-emerald-600", label: "Won", icon: Trophy },
  lost: { color: "bg-gray-50 text-gray-400", label: "Lost", icon: XCircle }
};

// Intake Status configuration (post-booking) - softer, more premium colors
const intakeStatusConfig = {
  waiting_for_intake: { color: "bg-slate-50 text-slate-600", label: "Waiting", icon: Clock },
  client_action_required: { color: "bg-amber-50 text-amber-600", label: "Needs Info", icon: AlertCircle },
  intake_received: { color: "bg-blue-50 text-blue-600", label: "Received", icon: Mail },
  in_review: { color: "bg-violet-50 text-violet-600", label: "In Review", icon: Target },
  scheduled: { color: "bg-indigo-50 text-indigo-600", label: "Scheduled", icon: Calendar },
  in_service: { color: "bg-orange-50 text-orange-600", label: "In Service", icon: PlayCircle },
  completed: { color: "bg-emerald-50 text-emerald-600", label: "Completed", icon: CheckCircle2 }
};

// Legacy status config for backwards compatibility
const statusConfig = leadStatusConfig;

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const { 
    subscriptionStatus,
    isTrialing,
    isActive,
    isPastDue,
    pastDueGraceExpired,
    pastDueHoursRemaining,
    leadsUsed: contextLeadsUsed,
    leadsLimit: contextLeadsLimit,
    stripeSubscriptionId,
    status: subscriptionStatusValue,
    refreshSubscription
  } = useSubscription();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forms, setForms] = useState([]);
  const [formsLoading, setFormsLoading] = useState(true);
  const [upcomingReminders, setUpcomingReminders] = useState([]);
  const [integrationSettings, setIntegrationSettings] = useState({});
  const [automations, setAutomations] = useState([]);
  const [setupChecklistDismissed, setSetupChecklistDismissed] = useState(false);
  
  // Onboarding State - use lowercase email for consistent localStorage keys
  const userEmailKey = user?.email?.toLowerCase() || '';
  
  // Skipped/Manually completed checklist items (stored in localStorage)
  const [skippedItems, setSkippedItems] = useState({});
  
  // Initialize skipped items from localStorage after userEmailKey is available
  useEffect(() => {
    if (userEmailKey) {
      const stored = localStorage.getItem(`heyintake_checklist_skipped_${userEmailKey}`);
      if (stored) {
        try {
          setSkippedItems(JSON.parse(stored));
        } catch (e) {
          setSkippedItems({});
        }
      }
    }
  }, [userEmailKey]);
  
  // Handler to skip/mark complete a checklist item
  const handleSkipOrComplete = (itemId) => {
    const newSkipped = { ...skippedItems, [itemId]: true };
    setSkippedItems(newSkipped);
    if (userEmailKey) {
      localStorage.setItem(`heyintake_checklist_skipped_${userEmailKey}`, JSON.stringify(newSkipped));
    }
  };
  
  const [onboardingDismissed, setOnboardingDismissed] = useState(() => {
    if (userEmailKey) {
      return localStorage.getItem(`heyintake_onboarding_dismissed_${userEmailKey}`) === 'true';
    }
    return false;
  });
  const [hasCopiedLink, setHasCopiedLink] = useState(() => {
    if (userEmailKey) {
      return localStorage.getItem(`heyintake_link_copied_${userEmailKey}`) === 'true';
    }
    return false;
  });
  const [hasVisitedFormBuilder, setHasVisitedFormBuilder] = useState(() => {
    if (userEmailKey) {
      return localStorage.getItem(`heyintake_form_builder_visited_${userEmailKey}`) === 'true';
    }
    return false;
  });

  // Re-check localStorage when user email changes (e.g., after login)
  useEffect(() => {
    if (userEmailKey) {
      const dismissedKey = `heyintake_onboarding_dismissed_${userEmailKey}`;
      const copiedKey = `heyintake_link_copied_${userEmailKey}`;
      const formBuilderKey = `heyintake_form_builder_visited_${userEmailKey}`;
      setOnboardingDismissed(localStorage.getItem(dismissedKey) === 'true');
      setHasCopiedLink(localStorage.getItem(copiedKey) === 'true');
      setHasVisitedFormBuilder(localStorage.getItem(formBuilderKey) === 'true');
    }
  }, [userEmailKey]);

  useEffect(() => {
    fetchData();
    fetchForms();
    fetchReminders();
    fetchIntegrationSettings();
    fetchAutomations();
    // Refresh subscription status when Dashboard loads to get latest data
    refreshSubscription();
  }, []);

  // REACTIVE: Refetch checklist data when window regains focus
  // This ensures the checklist updates after user configures settings/automations
  useEffect(() => {
    const handleFocus = () => {
      fetchIntegrationSettings();
      fetchAutomations();
      fetchForms();
      fetchData();
    };
    
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [token]);

  const fetchData = async () => {
    try {
      const [leadsRes, statsRes] = await Promise.all([
        axios.get(`${API}/leads`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/leads/stats/summary`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const fetchForms = async () => {
    try {
      const response = await axios.get(`${API}/business/forms`, { headers: { Authorization: `Bearer ${token}` } });
      setForms(response.data.forms || []);
    } catch (error) {
      console.error("Failed to fetch forms");
    } finally {
      setFormsLoading(false);
    }
  };

  const fetchReminders = async () => {
    try {
      const response = await axios.get(`${API}/leads/reminders/upcoming`, { headers: { Authorization: `Bearer ${token}` } });
      setUpcomingReminders(response.data.reminders || []);
    } catch (error) {
      console.error("Failed to fetch reminders");
    }
  };

  const fetchIntegrationSettings = async () => {
    try {
      const response = await axios.get(`${API}/workflow/settings`, { headers: { Authorization: `Bearer ${token}` } });
      setIntegrationSettings(response.data || {});
    } catch (error) {
      console.error("Failed to fetch integration settings");
    }
  };

  const fetchAutomations = async () => {
    try {
      const response = await axios.get(`${API}/automations`, { headers: { Authorization: `Bearer ${token}` } });
      setAutomations(response.data?.automations || []);
    } catch (error) {
      console.error("Failed to fetch automations");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Link copied to clipboard!");
    if (userEmailKey) {
      localStorage.setItem(`heyintake_link_copied_${userEmailKey}`, 'true');
      setHasCopiedLink(true);
    }
  };

  // Setup Checklist logic - NEW streamlined checklist
  // Items: Form, Test Lead, Add Contact, Terms & Conditions, E-Signature (optional), Automation (optional)
  const setupChecklist = useMemo(() => {
    const hasForm = forms.length > 0;
    const hasSubmission = leads.length > 0;
    const hasContact = leads.some(l => l.source === "contact" || l.source === "manual");
    // Check if terms/agreement is set up (from user/business settings)
    const hasTermsSetup = Boolean(user?.agreement_enabled || user?.agreement_text);
    // Check e-signature integration
    const hasESignSetup = Boolean(user?.signwell_api_key_encrypted || integrationSettings?.signing_link);
    // Check automations
    const hasAutomations = automations.length > 0;
    
    return [
      {
        id: "form_created",
        label: "Create your first form",
        completed: hasForm || skippedItems.form_created,
        optional: false,
        hint: "Design your intake form to capture leads",
        action: () => navigate("/form-builder"),
        skippable: false // Required step
      },
      {
        id: "test_lead",
        label: "Send your first test lead",
        completed: hasSubmission || skippedItems.test_lead,
        optional: false,
        hint: "Preview your form and submit a test entry",
        // Navigate to Form Builder Preview mode (not public form)
        action: hasForm 
          ? () => navigate(`/form-builder?formId=${forms[0]?.id}&preview=true`) 
          : () => navigate("/form-builder"),
        skippable: true
      },
      {
        id: "add_contact",
        label: "Add your first contact",
        completed: hasContact || skippedItems.add_contact,
        optional: false,
        hint: "Import or add a client manually",
        // Navigate to Contacts page (not Submissions)
        action: () => navigate("/contacts"),
        skippable: true
      },
      {
        id: "setup_terms",
        label: "Set up terms & conditions",
        completed: hasTermsSetup || skippedItems.setup_terms,
        optional: false,
        hint: "Add your agreement text in Settings",
        // Navigate to Settings with scroll to Agreement section
        action: () => navigate("/settings?tab=tools&section=agreement"),
        skippable: true
      },
      {
        id: "setup_esign",
        label: "Set up e-signature",
        completed: hasESignSetup || skippedItems.setup_esign,
        optional: true,
        hint: "Connect SignWell or add a signing link",
        // Navigate to Settings with scroll to Contract Signing section
        action: () => navigate("/settings?tab=tools&section=signing"),
        skippable: true
      },
      {
        id: "customize_branding",
        label: "Customize your branding",
        completed: Boolean(user?.brand_color) || skippedItems.customize_branding,
        optional: true,
        hint: "Set your brand color for forms and emails",
        action: () => navigate("/settings?tab=general&section=branding"),
        skippable: true
      },
      {
        id: "create_automation",
        label: "Create your first automation",
        completed: hasAutomations || skippedItems.create_automation,
        optional: true,
        hint: "Automate emails and follow-ups",
        action: () => navigate("/automations"),
        skippable: true
      }
    ];
  }, [user, forms, integrationSettings, automations, leads, navigate, skippedItems]);

  const completedSetupSteps = setupChecklist.filter(s => s.completed).length;
  const totalSetupSteps = setupChecklist.length;
  const setupProgress = (completedSetupSteps / totalSetupSteps) * 100;
  const allSetupComplete = completedSetupSteps === totalSetupSteps;
  
  // Show checklist only if not all complete and not dismissed
  const showSetupChecklist = !allSetupComplete && !setupChecklistDismissed;

  // Legacy onboarding checklist logic - keeping for backward compatibility
  const onboardingSteps = useMemo(() => {
    const hasLead = leads.length > 0;
    const hasReviewedLead = leads.some(l => l.status !== "new" || l.is_read);
    
    // Form step is complete when user has a form OR has visited Form Builder OR has leads
    const hasForm = forms.length > 0;
    const formStepComplete = hasForm || hasVisitedFormBuilder || hasLead;
    
    return [
      {
        id: "create_form",
        title: "Create your first form",
        description: "Customize your form fields to collect the right information",
        completed: formStepComplete,
        action: () => {
          // Mark as visited when they click
          if (userEmailKey) {
            localStorage.setItem(`heyintake_form_builder_visited_${userEmailKey}`, 'true');
          }
          navigate("/form-builder");
        },
        actionLabel: hasForm ? "Edit Form" : "Create Form"
      },
      {
        id: "copy_link",
        title: "Copy your form link",
        description: "Get your shareable link to send to potential clients",
        completed: (hasCopiedLink && hasForm) || hasLead, // If they have leads, they must have shared the link
        action: hasForm ? () => {
          const formUrl = `${window.location.origin}/form/${forms[0]?.slug}`;
          copyToClipboard(formUrl);
        } : null,
        actionLabel: "Copy Link"
      },
      {
        id: "receive_submission",
        title: "Receive your first submission",
        description: "Watch AI analyze and score incoming leads automatically",
        completed: hasLead,
        action: null,
        actionLabel: null
      },
      {
        id: "review_lead",
        title: "Review a lead",
        description: "Check the AI analysis and update the lead status",
        completed: hasReviewedLead,
        action: hasLead ? () => navigate("/pipeline") : null,
        actionLabel: "View Pipeline"
      }
    ];
  }, [forms, leads, hasCopiedLink, hasVisitedFormBuilder, userEmailKey, navigate]);

  const completedSteps = onboardingSteps.filter(s => s.completed).length;
  const onboardingProgress = (completedSteps / onboardingSteps.length) * 100;
  // Auto-dismiss when all steps complete OR when manually dismissed
  const showOnboarding = !onboardingDismissed && completedSteps < onboardingSteps.length;

  // Auto-dismiss if all steps are complete
  useEffect(() => {
    if (completedSteps === onboardingSteps.length && userEmailKey) {
      localStorage.setItem(`heyintake_onboarding_dismissed_${userEmailKey}`, 'true');
      setOnboardingDismissed(true);
    }
  }, [completedSteps, onboardingSteps.length, userEmailKey]);

  const dismissOnboarding = () => {
    setOnboardingDismissed(true);
    if (userEmailKey) {
      localStorage.setItem(`heyintake_onboarding_dismissed_${userEmailKey}`, 'true');
    }
  };

  // Recent leads for quick view
  const recentLeads = leads.slice(0, 5);

  return (
    <div className="min-h-screen bg-zinc-50 flex" >
      <AppSidebar user={user} onLogout={handleLogout} />

      <main className="flex-1 ml-64 p-6">
        <div className="max-w-6xl mx-auto">
          {/* Email Verification Banner */}
          {user && !user.email_verified && (
            <EmailVerificationBanner 
              email={user.email} 
              token={token}
            />
          )}
          
          {/* SLICE 5: Usage Warning Banner */}
          <UsageWarningBanner />
          
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground">
              Hello, {user?.business_name}
            </h1>
            <p className="text-muted-foreground mt-1">
              {leads.length === 0 && forms.length === 0 
                ? "Let's get started by creating your first intake form."
                : "Here's an overview of your CRM activity."
              }
            </p>
          </div>

          {/* Action Bar (with inline contextual message) */}
          <Card className="mb-8 rounded-2xl border-0 bg-[#fafafa]" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }} data-testid="action-bar">
            <CardContent className="p-6">
              <p className="text-base font-medium text-foreground mb-1">What would you like to do today?</p>
              {/* Inline contextual message - dynamic, one line only */}
              <p className="text-sm text-muted-foreground/80 mb-5">
                {(() => {
                  const totalSubmissions = leads.length;
                  const unreadLeads = leads.filter(l => !l.is_read && l.mode !== "post_booking").length;
                  const incompleteIntakes = leads.filter(l => 
                    l.mode === "post_booking" && 
                    ["waiting_for_intake", "client_action_required"].includes(l.intake_status)
                  ).length;
                  
                  // Check for inactivity (no submissions in last 7 days)
                  const sevenDaysAgo = new Date();
                  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
                  const recentSubmissions = leads.filter(l => {
                    const submissionDate = new Date(l.created_at);
                    return submissionDate > sevenDaysAgo;
                  }).length;
                  const hasInactivity = totalSubmissions > 0 && recentSubmissions === 0;

                  // Priority: no submissions > unread leads > incomplete intakes > inactivity
                  if (totalSubmissions === 0) {
                    return "No activity yet — send your intake form to your first client";
                  } else if (unreadLeads > 0) {
                    return `You have ${unreadLeads} lead${unreadLeads > 1 ? 's' : ''} waiting for a response`;
                  } else if (incompleteIntakes > 0) {
                    return `${incompleteIntakes} intake${incompleteIntakes > 1 ? 's' : ''} still incomplete — time to follow up`;
                  } else if (hasInactivity) {
                    return "No recent activity — send your intake to bring in new clients";
                  }
                  return "Your pipeline is looking good";
                })()}
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  onClick={() => navigate("/form-builder")} 
                  className="rounded-full bg-primary hover:bg-primary/90 text-white hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
                  data-testid="action-create-form"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Form
                </Button>
                <Button 
                  variant="ghost" 
                  onClick={() => navigate("/submissions")} 
                  className="rounded-full bg-white/80 border border-gray-200/80 hover:bg-white hover:border-gray-300 hover:shadow-sm hover:-translate-y-0.5 text-gray-600 transition-all duration-200"
                  data-testid="action-send-intake"
                >
                  <Send className="w-4 h-4 mr-2 opacity-70" />
                  Send Intake
                </Button>
                <Button 
                  variant="ghost" 
                  onClick={() => navigate("/contacts")} 
                  className="rounded-full bg-white/80 border border-gray-200/80 hover:bg-white hover:border-gray-300 hover:shadow-sm hover:-translate-y-0.5 text-gray-600 transition-all duration-200"
                  data-testid="action-add-contact"
                >
                  <UserPlus className="w-4 h-4 mr-2 opacity-70" />
                  Add Contact
                </Button>
                <Button 
                  variant="ghost" 
                  onClick={() => navigate("/automations")} 
                  className="rounded-full bg-white/80 border border-gray-200/80 hover:bg-white hover:border-gray-300 hover:shadow-sm hover:-translate-y-0.5 text-gray-600 transition-all duration-200"
                  data-testid="action-create-automation"
                >
                  <Zap className="w-4 h-4 mr-2 opacity-70" />
                  Create Automation
                </Button>
                {user?.team_enabled && (
                  <Button 
                    variant="ghost" 
                    onClick={() => navigate("/settings?tab=team")} 
                    className="rounded-full bg-white/80 border border-gray-200/80 hover:bg-white hover:border-gray-300 hover:shadow-sm hover:-translate-y-0.5 text-gray-600 transition-all duration-200"
                    data-testid="action-invite-team"
                  >
                    <UserPlus2 className="w-4 h-4 mr-2 opacity-70" />
                    Invite Team
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Setup Checklist - New Premium UX */}
          {allSetupComplete ? (
            <Card className="rounded-2xl border-0 bg-gradient-to-br from-green-50 to-emerald-50/50 mb-6" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }} data-testid="setup-complete-card">
              <div className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-green-800 text-lg">You're fully set up</h3>
                    <p className="text-sm text-green-700">Your intake system is live and ready to receive clients.</p>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => navigate("/submissions")} 
                      className="rounded-full border-green-200 text-green-700 hover:bg-green-100"
                    >
                      View submissions
                    </Button>
                    {forms.length > 0 && (
                      <Button 
                        size="sm" 
                        onClick={() => {
                          navigator.clipboard.writeText(`${window.location.origin}/form/${forms[0]?.slug}`);
                          toast.success("Form link copied!");
                        }}
                        className="rounded-full bg-green-600 hover:bg-green-700"
                      >
                        <Copy className="w-4 h-4 mr-2" />
                        Copy form link
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          ) : showSetupChecklist && (
            <Card className="rounded-2xl border-0 bg-white mb-6 overflow-hidden" style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }} data-testid="setup-checklist">
              {/* Premium Header */}
              <div className="bg-gradient-to-r from-slate-50 to-white p-6 border-b border-slate-100">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-foreground text-lg flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-primary" />
                      Quick Setup
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">Complete these steps to start capturing leads</p>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setSetupChecklistDismissed(true)} 
                    className="text-muted-foreground hover:text-foreground -mt-1 -mr-2"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                
                {/* Progress bar */}
                <div className="mt-4">
                  <div className="flex items-center justify-between text-xs mb-2">
                    <span className="text-muted-foreground font-medium">{completedSetupSteps} of {totalSetupSteps} complete</span>
                    <span className="text-primary font-medium">{Math.round(setupProgress)}%</span>
                  </div>
                  <Progress value={setupProgress} className="h-1.5" />
                </div>
              </div>

              {/* Checklist items - Premium minimal style */}
              <div className="p-4">
                <div className="space-y-1">
                  {setupChecklist.map((item) => {
                    const isSkipped = skippedItems[item.id];
                    const isCompleted = item.completed;
                    
                    return (
                      <div
                        key={item.id}
                        className={`group flex items-center gap-3 p-3 rounded-xl transition-all ${
                          isCompleted 
                            ? "bg-green-50/60" 
                            : "hover:bg-slate-50"
                        }`}
                        data-testid={`setup-item-${item.id}`}
                      >
                        {/* Checkbox */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isCompleted && item.skippable !== false) {
                              handleSkipOrComplete(item.id);
                            }
                          }}
                          className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                            isCompleted 
                              ? "bg-green-500 text-white" 
                              : "border-2 border-slate-300 hover:border-primary hover:bg-primary/5"
                          } ${item.skippable === false && !isCompleted ? "cursor-default" : "cursor-pointer"}`}
                          title={isCompleted ? "Completed" : item.skippable !== false ? "Mark as complete" : "Required step"}
                        >
                          {isCompleted && <Check className="w-3.5 h-3.5" />}
                        </button>
                        
                        {/* Content - clickable to perform action */}
                        <div 
                          className="flex-1 min-w-0 cursor-pointer"
                          onClick={item.action}
                        >
                          <div className="flex items-center gap-2">
                            <span className={`text-sm font-medium ${
                              isCompleted ? "text-green-700" : "text-foreground"
                            }`}>
                              {item.label}
                            </span>
                            {item.optional && (
                              <span className="text-[10px] text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">optional</span>
                            )}
                            {isSkipped && !item.completed && (
                              <span className="text-[10px] text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">skipped</span>
                            )}
                          </div>
                          {!isCompleted && item.hint && (
                            <p className="text-xs text-muted-foreground mt-0.5">{item.hint}</p>
                          )}
                        </div>
                        
                        {/* Actions */}
                        <div className="flex items-center gap-1">
                          {!isCompleted && item.skippable !== false && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSkipOrComplete(item.id);
                              }}
                              className="text-xs text-slate-400 hover:text-slate-600 px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              Skip
                            </button>
                          )}
                          <ChevronRight 
                            className={`w-4 h-4 flex-shrink-0 cursor-pointer ${
                              isCompleted ? "text-green-400" : "text-slate-300 group-hover:text-slate-400"
                            }`}
                            onClick={item.action}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Dynamic CTA based on checklist state */}
                <div className="mt-4 pt-4 border-t border-slate-100">
                  {(() => {
                    // CTA Logic: If no form → Create form, If form exists → Open form
                    const hasForm = forms.length > 0;
                    const formItem = setupChecklist.find(i => i.id === "form_created");
                    
                    if (!formItem?.completed && !hasForm) {
                      return (
                        <Button 
                          className="w-full rounded-full gap-2"
                          onClick={() => navigate("/form-builder")}
                          data-testid="setup-create-form-cta"
                        >
                          <FileEdit className="w-4 h-4" />
                          Create your first form
                        </Button>
                      );
                    } else if (hasForm) {
                      return (
                        <Button 
                          className="w-full rounded-full gap-2"
                          onClick={() => window.open(`${window.location.origin}/form/${forms[0]?.slug}`, '_blank')}
                          data-testid="setup-open-form-cta"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Open your form
                        </Button>
                      );
                    }
                    return null;
                  })()}
                </div>
              </div>
            </Card>
          )}

          {/* Payment Failed Warning Banner - for users in 48h grace period */}
          {isPastDue && !pastDueGraceExpired && (
            <Card className="rounded-2xl border-0 bg-red-50 mb-6" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }} data-testid="payment-failed-banner">
              <div className="p-5 flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-red-800">Payment Failed</p>
                  <p className="text-sm text-red-600">
                    Your payment method was declined. Update your payment info within {pastDueHoursRemaining || 48} hours to avoid service interruption.
                  </p>
                </div>
                <Button onClick={() => navigate("/settings?tab=billing")} className="rounded-full bg-red-600 hover:bg-red-700">
                  <CreditCard className="w-4 h-4 mr-2" />
                  Update Payment
                </Button>
              </div>
            </Card>
          )}

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Left Column - Split Summaries & Recent Activity */}
              <div className="lg:col-span-2 space-y-8">
                {/* Qualification Overview (Pre-Booking) */}
                <Card className="rounded-2xl border-0 bg-white transition-all duration-200 hover:shadow-[0_4px_12px_rgba(0,0,0,0.06)] hover:scale-[1.01]" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                  <CardHeader className="pb-4 pt-6 px-6">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base font-semibold flex items-center gap-2">
                        <Target className="w-5 h-5 text-blue-500" />
                        <span>Qualification Overview</span>
                        <Badge variant="outline" className="ml-2 text-xs font-normal text-muted-foreground border-gray-200">Pre-Booking</Badge>
                      </CardTitle>
                      <Button variant="ghost" size="sm" onClick={() => navigate("/submissions?mode=pre_booking")} className="rounded-full text-primary hover:text-primary hover:bg-primary/5 hover:-translate-y-0.5 transition-all duration-200">
                        View Leads
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="px-6 pb-6">
                    {stats?.pre_booking?.total > 0 ? (
                      <>
                        <div className="grid grid-cols-5 gap-3">
                          {Object.entries(leadStatusConfig).map(([key, config]) => {
                            const count = stats?.pre_booking?.status_breakdown?.[key] || 0;
                            return (
                              <div key={key} className={`p-3 rounded-xl ${config.color} text-center`}>
                                <config.icon className="w-4 h-4 mx-auto mb-2 opacity-80" />
                                <p className="text-2xl font-bold leading-none mb-1">{count}</p>
                                <p className="text-xs opacity-80">{config.label}</p>
                              </div>
                            );
                          })}
                        </div>
                        <div className="mt-6 pt-6 border-t border-gray-100 grid grid-cols-3 gap-6">
                          <div className="text-center">
                            <p className="text-3xl font-bold text-foreground leading-none mb-2">{stats?.pre_booking?.total || 0}</p>
                            <p className="text-sm text-muted-foreground/70">Total Leads</p>
                          </div>
                          <div className="text-center">
                            <p className="text-3xl font-bold text-blue-500 leading-none mb-2">{stats?.pre_booking?.avg_score || 0}</p>
                            <p className="text-sm text-muted-foreground/70">Avg Score</p>
                          </div>
                          <div className="text-center">
                            <p className="text-3xl font-bold text-emerald-500 leading-none mb-2">{stats?.pre_booking?.conversion_rate || 0}%</p>
                            <p className="text-sm text-muted-foreground/70">Conversion</p>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-10">
                        <Target className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-30" />
                        <p className="text-muted-foreground font-medium">No leads yet</p>
                        <p className="text-sm text-muted-foreground/70 mt-1">Create a pre-booking form to start qualifying leads</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Intake Overview (Post-Booking) */}
                <Card className="rounded-2xl border-0 bg-white transition-all duration-200 hover:shadow-[0_4px_12px_rgba(0,0,0,0.06)] hover:scale-[1.01]" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                  <CardHeader className="pb-4 pt-6 px-6">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base font-semibold flex items-center gap-2">
                        <ClipboardList className="w-5 h-5 text-violet-500" />
                        <span>Intake Overview</span>
                        <Badge variant="outline" className="ml-2 text-xs font-normal text-muted-foreground border-gray-200">Post-Booking</Badge>
                      </CardTitle>
                      <Button variant="ghost" size="sm" onClick={() => navigate("/submissions?mode=post_booking")} className="rounded-full text-primary hover:text-primary hover:bg-primary/5 hover:-translate-y-0.5 transition-all duration-200">
                        View Intakes
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="px-6 pb-6">
                    {stats?.post_booking?.total > 0 ? (
                      <>
                        <div className="grid grid-cols-7 gap-2">
                          {Object.entries(intakeStatusConfig).map(([key, config]) => {
                            const count = stats?.post_booking?.status_breakdown?.[key] || 0;
                            const isBottleneck = key === "client_action_required" && count > 0;
                            return (
                              <div key={key} className={`p-2 rounded-xl ${config.color} text-center ${isBottleneck ? 'ring-2 ring-amber-400 ring-offset-1' : ''}`}>
                                <config.icon className="w-4 h-4 mx-auto mb-2 opacity-80" />
                                <p className="text-xl font-bold leading-none mb-1">{count}</p>
                                <p className="text-xs truncate opacity-80">{config.label}</p>
                              </div>
                            );
                          })}
                        </div>
                        <div className="mt-6 pt-6 border-t border-gray-100 grid grid-cols-4 gap-4">
                          <div className="text-center">
                            <p className="text-3xl font-bold text-foreground leading-none mb-2">{stats?.post_booking?.total || 0}</p>
                            <p className="text-sm text-muted-foreground/70">Total Intakes</p>
                          </div>
                          <div className="text-center">
                            <p className="text-3xl font-bold text-amber-500 leading-none mb-2">{stats?.post_booking?.client_action_required || 0}</p>
                            <p className="text-sm text-muted-foreground/70">Needs Info</p>
                          </div>
                          <div className="text-center">
                            <p className="text-3xl font-bold text-violet-500 leading-none mb-2">{stats?.post_booking?.in_progress || 0}</p>
                            <p className="text-sm text-muted-foreground/70">In Progress</p>
                          </div>
                          <div className="text-center">
                            <p className="text-3xl font-bold text-emerald-500 leading-none mb-2">{stats?.post_booking?.completion_rate || 0}%</p>
                            <p className="text-sm text-muted-foreground/70">Completion</p>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-10">
                        <ClipboardList className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-30" />
                        <p className="text-muted-foreground font-medium">No client intakes yet</p>
                        <p className="text-sm text-muted-foreground/70 mt-1 mb-4">Start by sending your intake form to your first client.</p>
                        <Button onClick={() => navigate("/submissions")} variant="outline" size="sm" className="rounded-full hover:shadow-sm hover:-translate-y-0.5 transition-all duration-200">
                          <Send className="w-4 h-4 mr-2" />
                          Send Intake
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Recent Submissions */}
                <Card className="rounded-2xl border-0 bg-white transition-all duration-200 hover:shadow-[0_4px_12px_rgba(0,0,0,0.06)] hover:scale-[1.01]" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                  <CardHeader className="pb-4 pt-6 px-6">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base font-semibold flex items-center gap-2">
                        <Users className="w-5 h-5 text-primary" />
                        Recent Submissions
                      </CardTitle>
                      <Button variant="ghost" size="sm" onClick={() => navigate("/submissions")} className="rounded-full text-primary hover:text-primary hover:bg-primary/5 hover:-translate-y-0.5 transition-all duration-200">
                        View All
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="px-6 pb-6">
                    {recentLeads.length === 0 ? (
                      <div className="text-center py-10">
                        <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-30" />
                        <p className="text-muted-foreground font-medium mb-1">No submissions yet</p>
                        <p className="text-sm text-muted-foreground/70 mb-4">Share your form to start receiving leads</p>
                        <Button onClick={() => navigate("/form-builder")} className="rounded-full hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
                          Create Form
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {recentLeads.map((lead) => {
                          const isIntake = lead.mode === "post_booking";
                          const statusKey = isIntake 
                            ? (lead.intake_status || "waiting_for_intake")
                            : (lead.lead_status || lead.status || "new");
                          const config = isIntake ? intakeStatusConfig[statusKey] : leadStatusConfig[statusKey];
                          
                          return (
                            <div
                              key={lead.id}
                              onClick={() => navigate("/submissions")}
                              className="flex items-center gap-3 p-3 rounded-xl bg-gray-50/30 hover:bg-gray-50 cursor-pointer transition-all duration-200"
                            >
                              {isIntake ? (
                                <div className="w-10 h-10 rounded-lg bg-violet-50 text-violet-500 flex items-center justify-center">
                                  <ClipboardList className="w-5 h-5" />
                                </div>
                              ) : (
                                <div className={`w-10 h-10 rounded-lg ${lead.ai_score >= 70 ? 'bg-emerald-500' : lead.ai_score >= 50 ? 'bg-amber-500' : 'bg-red-400'} text-white flex items-center justify-center font-bold text-sm`}>
                                  {lead.ai_score ? Math.ceil(lead.ai_score) : "—"}
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="font-medium truncate">
                                  {lead.customer_name || lead.form_data?.["Full Name"] || lead.form_data?.["Name"] || "Unknown"}
                                </p>
                                <p className="text-sm text-muted-foreground/70 truncate">
                                  {lead.customer_email || lead.form_data?.["Email"] || lead.form_data?.["email"] || "No email"}
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className={`text-xs ${isIntake ? 'border-violet-200 text-violet-500' : 'border-blue-200 text-blue-500'}`}>
                                  {isIntake ? "Intake" : "Lead"}
                                </Badge>
                                {config && (
                                  <Badge className={`${config.color} rounded-full`}>
                                    {config.label}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Right Column - Reminders & Forms */}
              <div className="space-y-8">
                {/* Reminders */}
                <Card className="rounded-2xl border-0 bg-white transition-all duration-200 hover:shadow-[0_4px_12px_rgba(0,0,0,0.06)] hover:scale-[1.01]" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                  <CardHeader className="pb-4 pt-6 px-6">
                    <CardTitle className="text-base font-semibold flex items-center gap-2">
                      <Bell className="w-5 h-5 text-amber-400" />
                      Upcoming Reminders
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="px-6 pb-6">
                    {upcomingReminders.length === 0 ? (
                      <div className="text-center py-8">
                        <Calendar className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-30" />
                        <p className="text-sm text-muted-foreground font-medium">No upcoming reminders</p>
                        <p className="text-xs text-muted-foreground/70 mt-1">Set reminders from the Pipeline view</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {upcomingReminders.map((reminder) => (
                          <div 
                            key={reminder.reminder_id} 
                            className={`p-3 rounded-xl ${reminder.is_overdue ? 'bg-red-50' : 'bg-amber-50/70'}`}
                          >
                            <div className="flex items-start gap-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${reminder.is_overdue ? 'bg-red-100' : 'bg-amber-100'}`}>
                                {reminder.is_overdue ? <AlertCircle className="w-4 h-4 text-red-500" /> : <Clock className="w-4 h-4 text-amber-500" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm truncate">{reminder.lead_name}</p>
                                <p className="text-xs text-muted-foreground/70 truncate">{reminder.note}</p>
                                <p className={`text-xs mt-1 ${reminder.is_overdue ? 'text-red-500 font-medium' : 'text-amber-500'}`}>
                                  {reminder.is_overdue ? 'Overdue' : new Date(reminder.due_date).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Your Forms */}
                <Card className="rounded-2xl border-0 bg-white transition-all duration-200 hover:shadow-[0_4px_12px_rgba(0,0,0,0.06)] hover:scale-[1.01]" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                  <CardHeader className="pb-4 pt-6 px-6">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base font-semibold flex items-center gap-2">
                        <FileEdit className="w-5 h-5 text-primary" />
                        Your Forms
                      </CardTitle>
                      <Button variant="ghost" size="icon" onClick={() => navigate("/form-builder")} className="h-8 w-8 text-primary hover:text-primary hover:bg-primary/5 hover:-translate-y-0.5 rounded-full transition-all duration-200">
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="px-6 pb-6">
                    {formsLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground/50" />
                      </div>
                    ) : forms.length === 0 ? (
                      <div className="text-center py-8">
                        <FileEdit className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-30" />
                        <p className="text-sm text-muted-foreground font-medium mb-3">No forms created yet</p>
                        <Button onClick={() => navigate("/form-builder")} variant="outline" size="sm" className="rounded-full hover:shadow-sm hover:-translate-y-0.5 transition-all duration-200">
                          <Plus className="w-4 h-4 mr-1" />
                          Create Form
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <TooltipProvider>
                          {forms.map((form) => {
                            const formUrl = `${window.location.origin}/form/${form.slug}`;
                            return (
                              <div 
                                key={form.id} 
                                className="p-3 rounded-xl bg-gray-50/30 hover:bg-gray-50 transition-all duration-200"
                              >
                                <div className="flex items-center justify-between gap-2">
                                  <Link to={`/form-builder?form=${form.id}`} className="flex-1 min-w-0">
                                    <p className="font-medium truncate hover:text-primary transition-colors">{form.name}</p>
                                    <p className="text-xs text-muted-foreground/70">{form.lead_count || 0} leads</p>
                                  </Link>
                                  <div className="flex items-center gap-1">
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full hover:bg-gray-100 transition-colors" onClick={() => copyToClipboard(formUrl)}>
                                          <Copy className="w-4 h-4 text-gray-400" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>Copy link</TooltipContent>
                                    </Tooltip>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full hover:bg-gray-100 transition-colors" onClick={() => window.open(formUrl, '_blank')}>
                                          <ExternalLink className="w-4 h-4 text-gray-400" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>Open form</TooltipContent>
                                    </Tooltip>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </TooltipProvider>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
